import type { WidgetProviderProps } from '@lifi/widget-provider'
import { type JSX, type PropsWithChildren, use } from 'react'
import { WagmiContext } from 'wagmi'
import type { EthereumProviderConfig } from '../types.js'
import { EthereumBaseProvider } from './EthereumBaseProvider.js'
import { EthereumProviderValues } from './EthereumProviderValues.js'

interface EthereumWidgetProviderProps extends WidgetProviderProps {
  config?: EthereumProviderConfig
}

function useInEthereumContext(): boolean {
  const context = use(WagmiContext)
  return Boolean(context)
}

const EthereumWidgetProvider = ({
  forceInternalWalletManagement,
  isExternalContext = false,
  chains,
  config,
  children,
}: PropsWithChildren<EthereumWidgetProviderProps>) => {
  const inEthereumContext = useInEthereumContext()
  const effectiveIsExternal = isExternalContext || inEthereumContext

  if (inEthereumContext && !forceInternalWalletManagement) {
    return (
      <EthereumProviderValues
        isExternalContext={effectiveIsExternal}
        config={config}
      >
        {children}
      </EthereumProviderValues>
    )
  }

  return (
    <EthereumBaseProvider config={config} chains={chains}>
      <EthereumProviderValues
        isExternalContext={effectiveIsExternal}
        config={config}
      >
        {children}
      </EthereumProviderValues>
    </EthereumBaseProvider>
  )
}

export const EthereumProvider = (
  config?: EthereumProviderConfig
): ((props: PropsWithChildren<WidgetProviderProps>) => JSX.Element) => {
  return ({ children, ...props }: PropsWithChildren<WidgetProviderProps>) => (
    <EthereumWidgetProvider {...props} config={config}>
      {children}
    </EthereumWidgetProvider>
  )
}
